// Plain-text descriptions of a question for the gradebook's assignment page:
// its type, and the opening words of its prompt.

import type { RawQuestion } from '@/features/content/useComponent';
import {
  resolveRepresentativeVariableValues,
  substituteVariablesInContent,
} from '@/features/questions/randomVariables';

interface RawQuestionText {
  content?: unknown;
  content_section?: unknown;
  contentSection?: unknown;
  question_set?: unknown;
  questionSet?: unknown;
  type?: unknown;
  use_random_variables?: unknown;
  useRandomVariables?: unknown;
  variables?: unknown;
}

const TYPE_LABELS: Record<string, string> = {
  '0': 'Multiple choice',
  '1': 'Multiple choice',
  '2': 'Select all',
  '3': 'Numerical',
  '4': 'Exact answer',
  '5': 'Multi-part',
  '6': 'AI upload',
  AI_UPLOAD: 'AI upload',
  EXACT_ANSWER: 'Exact answer',
  MULTIPLE_CHOICE: 'Multiple choice',
  MULTI_PART: 'Multi-part',
  NUMERICAL_WITH_ERROR: 'Numerical',
  SELECT_ALL: 'Select all',
  UNKNOWN: 'Multiple choice',
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasParts(question: RawQuestionText) {
  const set = question.question_set ?? question.questionSet;
  return isRecord(set) && Array.isArray(set.questions) && set.questions.length > 0;
}

/** "Multiple choice", "Numerical", …: what kind of question this is. */
export function getQuestionTypeLabel(question: RawQuestionText): string {
  if (hasParts(question)) {
    return 'Multi-part';
  }
  const key = typeof question.type === 'number'
    ? String(question.type)
    : typeof question.type === 'string'
      ? question.type.trim().toUpperCase()
      : '';
  return TYPE_LABELS[key] ?? 'Multiple choice';
}

const ENTITIES: Record<string, string> = {
  '&amp;': '&',
  '&gt;': '>',
  '&lt;': '<',
  '&nbsp;': ' ',
  '&quot;': '"',
  '&#39;': "'",
};

/**
 * HTML as one line of plain text: tags dropped, common entities decoded, math
 * delimiters ($$ … $$, \( … \)) removed, whitespace collapsed. Cut at a word
 * boundary with an ellipsis when longer than maxLength.
 */
export function toPromptText(html: string, maxLength = 120): string {
  const text = html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&(amp|gt|lt|nbsp|quot|#39);/g, (entity) => ENTITIES[entity] ?? entity)
    .replace(/\$\$|\\\(|\\\)|\\\[|\\\]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (text.length <= maxLength) {
    return text;
  }
  const cut = text.slice(0, maxLength - 1);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > maxLength / 2 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}

const asWritten = (html: string) => html;

/**
 * Fills a question's random-value placeholders (`##name##`) with the example
 * values the chapter shows an instructor (the fixed representative seed), as
 * the editor's previews do. Each student still sees their own values. A
 * question marked as not using random values is left as written, as the
 * chapter leaves it.
 */
function exampleValuesFiller(question: RawQuestionText): (html: string) => string {
  if ((question.use_random_variables ?? question.useRandomVariables) === false) {
    return asWritten;
  }
  try {
    const values = resolveRepresentativeVariableValues(question as RawQuestion, hasParts(question));
    return values.length === 0 ? asWritten : (html) => substituteVariablesInContent(html, values);
  } catch {
    return asWritten;
  }
}

/** The opening words of a question's prompt, or '' when it has none. */
export function getQuestionPrompt(question: RawQuestionText): string {
  const fill = exampleValuesFiller(question);
  if (typeof question.content === 'string') {
    const text = toPromptText(fill(question.content));
    if (text) {
      return text;
    }
  }
  const section = question.content_section ?? question.contentSection;
  const units = isRecord(section)
    ? section.content_units ?? section.contentUnits
    : undefined;
  if (Array.isArray(units)) {
    for (const unit of units) {
      if (isRecord(unit) && typeof unit.text === 'string') {
        const text = toPromptText(fill(unit.text));
        if (text) {
          return text;
        }
      }
    }
  }
  return '';
}
